import { useEffect, useState } from 'react';
import { useAuth } from "@clerk/clerk-react";
import { Loader2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../utils/api';

import { getExperimentVariant, trackExperimentEvent } from '../utils/abTesting';
import { trackEvent, trackIntent } from '../utils/analytics';

export default function PricingTable() {
  const { getToken, isSignedIn } = useAuth();
  const { t, i18n } = useTranslation();
  const badgeVariant = getExperimentVariant('PRICING_BADGE', ['A', 'B']);

  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState('free');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [consentPulse, setConsentPulse] = useState(false);

  useEffect(() => {
    trackEvent('pricing_page_view');
    trackExperimentEvent('PRICING_BADGE', badgeVariant, 'view', {
      source: 'PricingTable'
    });
  }, [badgeVariant]);

  const prices = {
    plus: { monthly: 15, annual: 144, annualMonthly: 12 },
    pro: { monthly: 29, annual: 290, annualMonthly: 24.17 },
  };

  const uiLang = (i18n?.language || 'en').slice(0, 2);

  const billingFallbacks = {
    en: {
      startFree: 'START FREE',
      subscribePlus: 'SUBSCRIBE PLUS',
      subscribePro: 'SUBSCRIBE PRO',
      currentPlan: 'CURRENT PLAN',
      upgradePlus: 'UPGRADE TO PLUS',
      upgradePro: 'UPGRADE TO PRO',
      freePlan: 'FREE PLAN',
      includedBelow: 'LOWER PLAN',
      currentBadge: 'Current Plan',
      subSignedIn: 'Manage your current plan or upgrade when you need more volume.',
    },
    pt: {
      startFree: 'COMEÇAR GRÁTIS',
      subscribePlus: 'ASSINAR PLUS',
      subscribePro: 'ASSINAR PRO',
      currentPlan: 'PLANO ATUAL',
      upgradePlus: 'FAZER UPGRADE PARA PLUS',
      upgradePro: 'FAZER UPGRADE PARA PRO',
      freePlan: 'PLANO FREE',
      includedBelow: 'PLANO INFERIOR',
      currentBadge: 'Plano atual',
      subSignedIn: 'Gerencie seu plano atual ou faça upgrade quando precisar de mais volume.',
    },
    de: {
      startFree: 'KOSTENLOS STARTEN',
      subscribePlus: 'PLUS ABONNIEREN',
      subscribePro: 'PRO ABONNIEREN',
      currentPlan: 'AKTUELLER PLAN',
      upgradePlus: 'AUF PLUS UPGRADEN',
      upgradePro: 'AUF PRO UPGRADEN',
      freePlan: 'KOSTENLOSER PLAN',
      includedBelow: 'NIEDRIGERER PLAN',
      currentBadge: 'Aktueller Plan',
      subSignedIn: 'Verwalte deinen aktuellen Plan oder upgrade, wenn du mehr Volumen brauchst.',
    },
    fr: {
      startFree: 'COMMENCER GRATUITEMENT',
      subscribePlus: 'S’ABONNER À PLUS',
      subscribePro: 'S’ABONNER À PRO',
      currentPlan: 'PLAN ACTUEL',
      upgradePlus: 'PASSER À PLUS',
      upgradePro: 'PASSER À PRO',
      freePlan: 'PLAN GRATUIT',
      includedBelow: 'PLAN INFÉRIEUR',
      currentBadge: 'Plan actuel',
      subSignedIn: 'Gérez votre plan actuel ou passez à une offre supérieure si vous avez besoin de plus de volume.',
    },
    es: {
      startFree: 'EMPEZAR GRATIS',
      subscribePlus: 'SUSCRIBIRSE A PLUS',
      subscribePro: 'SUSCRIBIRSE A PRO',
      currentPlan: 'PLAN ACTUAL',
      upgradePlus: 'ACTUALIZAR A PLUS',
      upgradePro: 'ACTUALIZAR A PRO',
      freePlan: 'PLAN FREE',
      includedBelow: 'PLAN INFERIOR',
      currentBadge: 'Plan actual',
      subSignedIn: 'Gestiona tu plan actual o mejora cuando necesites más volumen.',
    },
  };

  const bf = billingFallbacks[uiLang] || billingFallbacks.en;

  const legalHref = (slug) => ['en', 'pt', 'es', 'fr', 'de'].includes(uiLang) ? `/${uiLang}/${slug}` : `/${slug}`;
  const consentCopy = {
    en: {
      labelBefore: 'I agree to the',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      refund: 'Refund Policy',
      and: 'and',
      labelMiddle: ', and understand the billing and cancellation terms.',
      error: 'You must accept the legal terms before continuing to sign-up or checkout.'
    },
    pt: {
      labelBefore: 'Eu concordo com os',
      terms: 'Termos de Serviço',
      privacy: 'Política de Privacidade',
      refund: 'Política de Reembolso',
      and: 'e',
      labelMiddle: ', e compreendo as condições de cobrança e cancelamento.',
      error: 'Você precisa aceitar os termos legais antes de continuar para o cadastro ou checkout.'
    },
    es: {
      labelBefore: 'Acepto los',
      terms: 'Términos del Servicio',
      privacy: 'Política de Privacidad',
      refund: 'Política de Reembolso',
      and: 'y',
      labelMiddle: ', y entiendo las condiciones de facturación y cancelación.',
      error: 'Debes aceptar los términos legales antes de continuar al registro o checkout.'
    },
    fr: {
      labelBefore: 'J’accepte les',
      terms: 'Conditions d’utilisation',
      privacy: 'Politique de confidentialité',
      refund: 'Politique de remboursement',
      and: 'et',
      labelMiddle: ', et je comprends les conditions de facturation et d’annulation.',
      error: 'Vous devez accepter les documents juridiques avant de continuer vers l’inscription ou le checkout.'
    },
    de: {
      labelBefore: 'Ich stimme den',
      terms: 'Nutzungsbedingungen',
      privacy: 'Datenschutzrichtlinie',
      refund: 'Rückerstattungsrichtlinie',
      and: 'und',
      labelMiddle: 'zu und verstehe die Bedingungen zu Abrechnung und Kündigung.',
      error: 'Du musst die rechtlichen Dokumente akzeptieren, bevor du mit Registrierung oder Checkout fortfährst.'
    }
  }[uiLang] || {
    labelBefore: 'I agree to the',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    refund: 'Refund Policy',
    and: 'and',
    labelMiddle: ', and understand the billing and cancellation terms.',
    error: 'You must accept the legal terms before continuing to sign-up or checkout.'
  };

  useEffect(() => {
    const fetchPlan = async () => {
      if (!isSignedIn) {
        setCurrentPlan('free');
        return;
      }

      try {
        const res = await authFetch('/api/user-info', {}, getToken);
        const data = await res.json();
        if (res.ok) {
          setCurrentPlan(data.plan_tier || 'free');
        }
      } catch (e) {
        console.error('PricingTable plan fetch error:', e);
      }
    };

    fetchPlan();
  }, [isSignedIn, getToken]);

  const normalizePlan = (plan) => {
    if (plan === 'owner') return 'pro';
    return plan || 'free';
  };

  const activePlan = normalizePlan(currentPlan);
  const isCurrentPlan = (plan) => normalizePlan(plan) === activePlan;

  const nextUpgradePlan =
    activePlan === 'free'
      ? 'plus'
      : activePlan === 'plus'
      ? 'pro'
      : null;

  const isDimmedPlan = (plan) => {
    if (activePlan === 'free') return plan === 'free';
    if (activePlan === 'plus') return plan === 'free';
    if (activePlan === 'pro') return plan === 'free' || plan === 'plus';
    return false;
  };

  const isPrimaryHighlight = (plan) => {
    if (nextUpgradePlan) return plan === nextUpgradePlan;
    return activePlan === 'pro' && plan === 'pro';
  };

  const getButtonLabel = (plan) => {
    if (!isSignedIn) {
      if (plan === 'free') return t('landing.pricing.free.cta', { defaultValue: bf.startFree });
      if (plan === 'plus') return t('landing.pricing.plus.cta', { defaultValue: bf.subscribePlus });
      return t('landing.pricing.pro.cta', { defaultValue: bf.subscribePro });
    }

    if (isCurrentPlan(plan)) {
      return t('dashboard.billing.currentPlan', { defaultValue: bf.currentPlan });
    }

    if (activePlan === 'free') {
      if (plan === 'plus') return t('dashboard.billing.upgradePlus', { defaultValue: bf.upgradePlus });
      if (plan === 'pro') return t('dashboard.billing.upgradePro', { defaultValue: bf.upgradePro });
      return t('dashboard.billing.freePlan', { defaultValue: bf.freePlan });
    }

    if (activePlan === 'plus') {
      if (plan === 'pro') return t('dashboard.billing.upgradePro', { defaultValue: bf.upgradePro });
      if (plan === 'free') return t('dashboard.billing.freePlan', { defaultValue: bf.freePlan });
      return t('dashboard.billing.currentPlan', { defaultValue: bf.currentPlan });
    }

    if (activePlan === 'pro') {
      if (plan === 'pro') return t('dashboard.billing.currentPlan', { defaultValue: bf.currentPlan });
      return t('dashboard.billing.includedBelow', { defaultValue: bf.includedBelow });
    }

    return t('landing.pricing.free.cta', { defaultValue: bf.startFree });
  };

  const isButtonDisabled = (plan) => {
    if (loading) return false;
    if (isCurrentPlan(plan)) return true;

    if (activePlan === 'plus' && plan === 'free') return true;
    if (activePlan === 'pro' && (plan === 'plus' || plan === 'free')) return true;
    if (activePlan === 'free' && plan === 'free') return true;

    return false;
  };

  const handleCheckout = async (plan) => {
    if (isButtonDisabled(plan)) return;

    if (plan !== 'free' && !consentAccepted) {
      setConsentError(consentCopy.error);
      setConsentPulse(true);
      window.setTimeout(() => setConsentPulse(false), 1800);
      window.setTimeout(() => {
        document.getElementById('billing-legal-consent')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 30);
      return;
    }

    setConsentError('');

    trackIntent(plan, 'checkout_intent', { billing, source: 'PricingTable' });
    trackEvent('signup_started', { location: 'pricing_' + plan });

    if (plan === 'plus') {
      trackExperimentEvent('PRICING_BADGE', badgeVariant, 'checkout_click', {
        plan: 'plus',
        billing
      });
    }

    if (plan === 'free') {
      window.location.href = '/dashboard';
      return;
    }

    if (!isSignedIn) {
      sessionStorage.setItem('pending_checkout', JSON.stringify({ planType: plan, billing, consentAccepted: true, consentVersion: '2026-04-legal', consentTimestamp: new Date().toISOString() }));
      window.location.href = `/sign-in?checkout=${plan}&billing=${billing}`;
      return;
    }

    setLoading(`${plan}_${billing}`);
    try {
      const res = await authFetch(
        '/api/create-checkout-session',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planType: plan, billing, consentAccepted: true, consentVersion: '2026-04-legal', consentTimestamp: new Date().toISOString() }),
        },
        getToken
      );

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setLoading(null);
    }
  };

  const isLoadingKey = (plan) => loading === `${plan}_${billing}`;

  const annualSavingsPlus = Math.round(((15 * 12) - 144) / (15 * 12) * 100);
  const annualSavingsPro = Math.round(((29 * 12) - 290) / (29 * 12) * 100);

  const getButtonClass = (plan) => {
    if (isCurrentPlan(plan) || isDimmedPlan(plan)) return 'btn-plan btn-plan-disabled';
    if (plan === nextUpgradePlan) return 'btn-plan btn-plan-gold';
    return 'btn-plan btn-plan-ghost';
  };

  const getCardClass = (plan) => {
    const classes = ['price-card'];

    if (isPrimaryHighlight(plan)) {
      classes.push('featured');
    }

    if (isCurrentPlan(plan)) {
      classes.push('current-plan-card');
    }

    return classes.join(' ');
  };

  const getCardStyle = (plan) => {
    if (isDimmedPlan(plan)) {
      return {
        opacity: 0.45,
        filter: 'grayscale(0.15)',
      };
    }

    if (isPrimaryHighlight(plan)) {
      return {
        opacity: 1,
        zIndex: plan === 'plus' ? 2 : 1
      };
    }

    return {
      opacity: 0.9,
    };
  };

  const showBadge = (plan) => {
    if (plan === 'plus' && activePlan === 'free') {
      return badgeVariant === 'B'
        ? t('landing.mini.ui.recommended_alt', { defaultValue: 'Best Value' })
        : t('landing.mini.ui.recommended', { defaultValue: 'Most Popular' });
    }

    if (isCurrentPlan(plan)) {
      return t('dashboard.billing.currentBadge', { defaultValue: bf.currentBadge });
    }

    if (plan === 'pro' && activePlan === 'plus') {
      return t('landing.pricing.pro.badgePower', { defaultValue: 'For Power Users' });
    }

    return null;
  };

  return (
    <section id="pricing" className="pricing-section">
      <div className="section-label">
        {t('landing.pricing.label', { defaultValue: 'Pricing' })}
      </div>

      <h2 className="section-title">
        {t('landing.pricing.title', { defaultValue: 'Invest in your Growth.' })}
      </h2>

      <p className="section-sub">
        {isSignedIn
          ? t('dashboard.billing.subSignedIn', {
              defaultValue: bf.subSignedIn,
            })
          : t('landing.pricing.sub', { defaultValue: 'Choose your plan and start creating today.' })}
      </p>

      <div
        className="billing-toggle"
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '2.5rem',
          flexWrap: 'wrap',
        }}
      >
        <button
          className={`pill-btn ${billing === 'monthly' ? 'active' : ''}`}
          onClick={() => setBilling('monthly')}
          style={{ fontSize: '0.92rem', padding: '0.5rem 1.25rem' }}
        >
          {t('landing.pricing.monthly', { defaultValue: 'Monthly' })}
        </button>

        <button
          className={`pill-btn ${billing === 'annual' ? 'active' : ''}`}
          onClick={() => setBilling('annual')}
          style={{ fontSize: '0.92rem', padding: '0.5rem 1.25rem' }}
        >
          {t('landing.pricing.annual', { defaultValue: 'Annual' })}
          <span
            style={{
              marginLeft: '0.5rem',
              fontSize: '0.78rem',
              background: 'var(--gold)',
              color: '#000',
              borderRadius: '6px',
              padding: '2px 9px',
              fontWeight: 800,
              boxShadow: '0 2px 4px rgba(212,175,55,0.2)'
            }}
          >
            {t('landing.pricing.saveUp', { defaultValue: 'Save 20%' })}
          </span>
        </button>
      </div>

      <div id="billing-legal-consent" style={{ maxWidth: '760px', margin: '0 auto 1.4rem', textAlign: 'left', padding: '0.9rem 1rem', borderRadius: '16px', border: consentPulse || consentError ? '1px solid rgba(239, 68, 68, 0.45)' : '1px solid rgba(201, 162, 39, 0.16)', background: consentPulse || consentError ? 'rgba(127, 29, 29, 0.14)' : 'rgba(16,16,16,0.55)', boxShadow: consentPulse ? '0 0 0 3px rgba(239,68,68,0.08)' : 'none', transition: 'all 0.25s ease' }}>
        <label style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start', color: 'var(--muted)', lineHeight: 1.6, fontSize: '0.95rem' }}>
          <input
            type="checkbox"
            checked={consentAccepted}
            onChange={(e) => {
              setConsentAccepted(e.target.checked);
              if (e.target.checked) setConsentError('');
            }}
            style={{ marginTop: '0.28rem' }}
          />
          <span>
            {consentCopy.labelBefore} <a href={legalHref('terms')} target="_blank" rel="noreferrer">{consentCopy.terms}</a>, <a href={legalHref('privacy')} target="_blank" rel="noreferrer">{consentCopy.privacy}</a> {consentCopy.and} <a href={legalHref('refund-policy')} target="_blank" rel="noreferrer">{consentCopy.refund}</a>{consentCopy.labelMiddle}
          </span>
        </label>
        {consentError ? (
          <div style={{ marginTop: '0.65rem', color: '#fca5a5', fontSize: '0.9rem' }}>{consentError}</div>
        ) : null}
      </div>

      <div className="pricing-grid">
        <div className={getCardClass('free')} style={getCardStyle('free')}>
          <div className="plan-name">{t('landing.pricing.free.name', { defaultValue: 'Free' })}</div>
          <div className="price-amount">
            <span className="price-currency">$</span>
            <span className="price-value">0</span>
            <span className="price-period">/mo</span>
          </div>
          <p className="price-desc">
            {t('landing.pricing.free.desc', {
              defaultValue: 'Test the magic. No card, no commitment.',
            })}
          </p>
          <ul className="price-features">
            <li><span className="check">✓</span> {t('landing.pricing.free.f1', { defaultValue: '5 generations per day' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.free.f2', { defaultValue: 'Basic Bio, Caption & Hashtags' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.free.f3', { defaultValue: 'English & Portuguese only' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.free.f4', { defaultValue: 'Save up to 10 history items' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.free.f6', { defaultValue: '7-day retention' })}</li>
            <li><span className="cross">✕</span> {t('landing.pricing.free.f8', { defaultValue: '0 Brand Profiles' })}</li>
            <li><span className="cross">✕</span> {t('landing.pricing.free.f5', { defaultValue: 'Standard Hashtag sets' })}</li>
          </ul>
          <button
            onClick={() => handleCheckout('free')}
            disabled={isButtonDisabled('free')}
            className={getButtonClass('free')}
          >
            {getButtonLabel('free')}
          </button>
        </div>

        <div className={getCardClass('plus')} style={getCardStyle('plus')}>
          {showBadge('plus') && <div className="plan-badge" style={{ backgroundColor: 'var(--gold)', color: '#000', fontWeight: 'bold', padding: '0.4rem 1.2rem', boxShadow: '0 4px 10px rgba(212,175,55,0.3)', border: 'none', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{showBadge('plus')}</div>}

          <div className="plan-name">{t('landing.pricing.plus.name', { defaultValue: 'Plus' })}</div>

          <div className="price-amount">
            <span className="price-currency">$</span>
            <span className="price-value">
              {billing === 'annual' ? prices.plus.annualMonthly : prices.plus.monthly}
            </span>
            <span className="price-period">/mo</span>
          </div>

          {billing === 'annual' && (
            <div style={{ fontSize: '0.82rem', color: 'var(--gold)', marginBottom: '0.3rem' }}>
              ${prices.plus.annual}/year · Save {annualSavingsPlus}%
            </div>
          )}

          <p className="price-desc">
            {t('landing.pricing.plus.tagline', { defaultValue: 'The essential toolkit to grow your brand and sound human.' })}
          </p>

          <ul className="price-features">
            <li><span className="check" style={{color: 'var(--gold)', fontWeight: 'bold'}}>✓</span> {t('landing.pricing.plus.f1', { defaultValue: '250 premium generations / mo' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f2', { defaultValue: 'Advanced Bio, Caption & Hashtags' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f3', { defaultValue: 'All 6 premium languages' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f4', { defaultValue: 'Refine feature unlocked' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f5', { defaultValue: 'Generate 3 Variations at once' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f6', { defaultValue: 'Unlimited History & Saved Library' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f8', { defaultValue: '1 Brand Profile' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.plus.f7', { defaultValue: 'Priority email support' })}</li>
          </ul>

          <button
            onClick={() => handleCheckout('plus')}
            disabled={isButtonDisabled('plus') || isLoadingKey('plus')}
            className={getButtonClass('plus')}
          >
            {isLoadingKey('plus') ? (
              <Loader2 size={14} className="spinner" />
            ) : (
              getButtonLabel('plus')
            )}
          </button>
        </div>

        <div className={getCardClass('pro')} style={getCardStyle('pro')}>
          {showBadge('pro') && <div className="plan-badge">{showBadge('pro')}</div>}

          <div className="plan-name">{t('landing.pricing.pro.name', { defaultValue: 'Pro' })}</div>

          <div className="price-amount">
            <span className="price-currency">$</span>
            <span className="price-value">
              {billing === 'annual' ? prices.pro.annualMonthly : prices.pro.monthly}
            </span>
            <span className="price-period">/mo</span>
          </div>

          {billing === 'annual' && (
            <div style={{ fontSize: '0.82rem', color: 'var(--gold)', marginBottom: '0.3rem' }}>
              ${prices.pro.annual}/year · Save {annualSavingsPro}%
            </div>
          )}

          <p className="price-desc">
            {t('landing.pricing.pro.tagline', { defaultValue: 'Scale your content machine. Built for agencies and teams.' })}
          </p>

          <ul className="price-features">
            <li><span className="check">✓</span> {t('landing.pricing.pro.f1', { defaultValue: '750 premium generations / mo' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f2', { defaultValue: 'Advanced Bio, Caption & Hashtags' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f3', { defaultValue: 'All 6 premium languages' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f4', { defaultValue: 'Early access to new languages' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f5', { defaultValue: 'Refine + 3 Variations included' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f6', { defaultValue: 'Unlimited History & Saved Library' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f8', { defaultValue: '5 Brand Profiles' })}</li>
            <li><span className="check">✓</span> {t('landing.pricing.pro.f7', { defaultValue: 'VIP priority support' })}</li>
          </ul>

          <button
            onClick={() => handleCheckout('pro')}
            disabled={isButtonDisabled('pro') || isLoadingKey('pro')}
            className={getButtonClass('pro')}
          >
            {isLoadingKey('pro') ? (
              <Loader2 size={14} className="spinner" />
            ) : (
              getButtonLabel('pro')
            )}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '2.5rem', maxWidth: '600px', margin: '2.5rem auto 0', textAlign: 'center' }}>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          {t('landing.pricing.smallPrint', {
            defaultValue:
              'A generation is counted each time new content is created. Refine counts as 1 generation. 3 Variations counts as 2 generations. Free renews daily. Paid plans renew monthly based on the billing cycle. Unused generations do not roll over.',
          })}
        </p>

        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          <Check size={13} style={{ display: 'inline', marginRight: '5px', color: 'var(--gold)' }} />
          {t('landing.pricing.trustLine', { defaultValue: 'Clear limits, no hidden fees, cancel anytime.' })}
        </p>

        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          <Check size={13} style={{ display: 'inline', marginRight: '5px', color: 'var(--gold)' }} />
          {t('landing.pricing.socialProofLine', { defaultValue: 'Trusted by 10,000+ creators and brands.' })}
        </p>

        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {t('landing.pricing.expandLine', {
            defaultValue: 'Need more volume later? We can expand limits as CaptionScript grows.',
          })}
        </p>
      </div>
    </section>
  );
}
